import i18next from "../../i18n";
import { OrderResponse } from "../../types";
import { paymentMethodInCurrentLocale } from "../../utils";
import { OfferConditionsForm } from "./offerConditionsSchema";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JointStatus = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MaterialOfferState = Record<string, any>;

export const EMPTY_CONDITIONS: OfferConditionsForm = {
  offerValidity: "",
  paymentMethod: "",
  paymentTerm: "",
  leadTime: "",
  deliveryToBuyer: true,
  deliveryCostIncluded: true,
  additionalDeliveryCost: "",
  deliveryCostCurrency: "",
  pickupAddress: "",
  generalComments: "",
};

export interface InitialOfferState {
  materialOffers: Record<string, MaterialOfferState>;
  formValues: OfferConditionsForm;
  fileName: string;
  expandedMaterial: string | false;
}

/**
 * Pure derivation of the submission screen's initial state from the loaded
 * order(s) and (for joint orders) any prior offer status: the per-material
 * offer rows, the general-conditions form values, the "existing file" label,
 * and which material panel starts expanded. Extracted from
 * `useOfferSubmission`'s own pre-populate effect (Task 5 finding 2) so the
 * business rule — "prior joint offer wins, otherwise fall back to the
 * order's own payment defaults" — is unit-testable without rendering a
 * component or a hook. The caller still owns the `useEffect`/`reset`/
 * `setState` wiring; this just computes what to apply.
 */
export const buildInitialOfferState = (
  orders: OrderResponse[],
  isJointOrder: boolean,
  jointOrderOfferStatus: JointStatus
): InitialOfferState => {
  const materialOffers: Record<string, MaterialOfferState> = {};
  const baseValues: OfferConditionsForm = {
    ...EMPTY_CONDITIONS,
    // Default the seller's payment method to the buyer's, resolved to the seller's UI language so it
    // matches PAYMENT_METHOD_LIST (payment methods are stored as translated strings).
    paymentMethod: paymentMethodInCurrentLocale(orders[0]?.paymentMethod),
    paymentTerm: orders[0]?.paymentTerm?.toString() || "",
    deliveryCostCurrency: orders[0]?.currency || "USD",
  };

  if (isJointOrder && jointOrderOfferStatus) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jointOrderOfferStatus.ordersWithOffers.forEach((orderStatus: any) => {
      if (orderStatus.offer) {
        materialOffers[orderStatus.orderId] = {
          price: orderStatus.offer.price,
          currency: orderStatus.offer.currency || "USD",
          comment: orderStatus.offer.comment || "",
          expireDate: orderStatus.offer.expireDate || "",
          isComplete: true,
          isExisting: true,
        };
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jointOrderOfferStatus.ordersWithoutOffers.forEach((orderStatus: any) => {
      const orderItem = orders.find((o) => o.id === orderStatus.orderId);
      materialOffers[orderStatus.orderId] = {
        price: "",
        currency: orderItem?.currency || "USD",
        comment: "",
        isComplete: false,
        isExisting: false,
      };
    });

    const priorOffer =
      jointOrderOfferStatus.ordersWithOffers.length > 0
        ? jointOrderOfferStatus.ordersWithOffers[0].offer
        : undefined;

    const formValues: OfferConditionsForm = priorOffer
      ? {
          offerValidity: priorOffer.offerValidity || "",
          paymentMethod:
            priorOffer.paymentMethod ||
            paymentMethodInCurrentLocale(orders[0]?.paymentMethod),
          paymentTerm:
            priorOffer.paymentTerm?.toString() ||
            orders[0]?.paymentTerm?.toString() ||
            "",
          leadTime: priorOffer.leadTime || "",
          deliveryToBuyer: priorOffer.deliveryPlace === "SELLER",
          deliveryCostIncluded:
            priorOffer.deliveryPlace === "SELLER" &&
            (priorOffer.deliveryCost ?? 0) === 0,
          additionalDeliveryCost: priorOffer.deliveryCost
            ? priorOffer.deliveryCost.toString()
            : "",
          pickupAddress: priorOffer.pickupAddress || "",
          deliveryCostCurrency:
            priorOffer.deliveryCostCurrency ||
            priorOffer.currency ||
            baseValues.deliveryCostCurrency,
          generalComments: priorOffer.generalComments || "",
        }
      : baseValues;

    const expandedMaterial: string | false =
      jointOrderOfferStatus.ordersWithoutOffers &&
      jointOrderOfferStatus.ordersWithoutOffers.length > 0
        ? jointOrderOfferStatus.ordersWithoutOffers[0].orderId
        : orders[0]?.id || false;

    return {
      materialOffers,
      formValues,
      fileName: priorOffer?.fileURL ? i18next.t("offer.existingFile") : "",
      expandedMaterial,
    };
  }

  orders.forEach((orderItem) => {
    materialOffers[orderItem.id] = {
      price: "",
      currency: orderItem.currency || "USD",
      comment: "",
      isComplete: false,
      isExisting: false,
    };
  });

  return {
    materialOffers,
    formValues: baseValues,
    fileName: "",
    expandedMaterial: orders[0]?.id || false,
  };
};
